import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Inbox,
  Plus,
  X,
  Clock,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Flame,
  Search,
} from 'lucide-react';
import { apiFetch, isApiError } from '../lib/api';
import { formatDateTime, formatRelativeOrAbsolute } from '../lib/datetime';
import {
  addTicketComment,
  createTicket,
  getTicket,
  listSiteAgents,
  listTickets,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  updateTicket,
  type TicketDetail,
  type TicketEventRow,
  type TicketListFilters,
  type TicketPriority,
  type TicketRow,
  type TicketStatus,
} from '../lib/tickets-api';
import {
  Button,
  Badge,
  Input,
  Textarea,
  Label,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui';
import styles from './Analytics.module.css';
import own from './Tickets.module.css';

interface SiteLite { id: string; name: string }
interface AgentLite { agentId: string; displayName: string }

const STATUS_VIEWS: Array<{ value: NonNullable<TicketListFilters['status']>; label: string }> = [
  { value: 'active', label: 'Active (open + pending)' },
  ...TICKET_STATUSES.map((s) => ({ value: s, label: s })),
  { value: 'all', label: 'All' },
];

function StatusBadge({ status }: { status: TicketStatus }) {
  switch (status) {
    case 'open':
      return (
        <Badge variant="outline" className="text-sky-400 border-sky-400/30 bg-sky-400/10 text-[10px] font-semibold uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-sky-400 mr-1.5 inline-block" />
          open
        </Badge>
      );
    case 'pending':
      return (
        <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10 text-[10px] font-semibold uppercase">
          <Clock size={10} className="mr-1" />
          pending
        </Badge>
      );
    case 'resolved':
      return (
        <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 bg-emerald-400/10 text-[10px] font-semibold uppercase">
          <CheckCircle2 size={10} className="mr-1" />
          resolved
        </Badge>
      );
    case 'closed':
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground border-border text-[10px] font-semibold uppercase">
          closed
        </Badge>
      );
  }
}

function PriorityBadge({ priority }: { priority: TicketPriority }) {
  switch (priority) {
    case 'urgent':
      return (
        <Badge variant="destructive" className="text-[10px] uppercase font-semibold">
          <Flame size={10} className="mr-1 fill-current" />
          urgent
        </Badge>
      );
    case 'high':
      return (
        <Badge variant="warning" className="text-[10px] uppercase font-semibold">
          <AlertCircle size={10} className="mr-1" />
          high
        </Badge>
      );
    case 'normal':
      return (
        <Badge variant="secondary" className="text-[10px] uppercase font-medium">
          normal
        </Badge>
      );
    case 'low':
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground text-[10px] uppercase">
          low
        </Badge>
      );
  }
}

// Ticket inbox. Any agent works the tickets of the sites they can see; the
// server scopes the list. The selected ticket lives in the URL (/tickets/:id)
// so it can be linked from a conversation.
export function Tickets() {
  const { id: selectedId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sites, setSites] = useState<SiteLite[]>([]);
  const [filters, setFilters] = useState<TicketListFilters>({ status: 'active', page: 1 });
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<TicketRow[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    apiFetch<{ sites: SiteLite[] }>('/api/admin/sites')
      .then((r) => setSites(r.sites))
      .catch(() => { /* site filter just stays empty */ });
  }, []);

  // Debounce the search box so each keystroke isn't a request.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: query.trim() || undefined, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listTickets(filters);
      setRows(r.items);
      setTotal(r.total);
      setLimit(r.limit);
    } catch {
      setError('Could not load tickets.');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { void load(); }, [load]);

  const set = (patch: Partial<TicketListFilters>) => setFilters((f) => ({ ...f, ...patch, page: 1 }));
  const page = filters.page ?? 1;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <div className="flex items-center gap-2 mb-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
            <span>Support Desk · Ticket Matrix</span>
          </div>
          <h1 className={styles.title}>Customer & Internal Tickets</h1>
        </div>
        <div className={styles.controls}>
          <label className={styles.control}>
            <span className={styles.controlLabel}>Site</span>
            <select className={styles.select} value={filters.siteId ?? ''} onChange={(e) => set({ siteId: e.target.value || undefined })}>
              <option value="">All sites</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className={styles.control}>
            <span className={styles.controlLabel}>Status</span>
            <select className={styles.select} value={filters.status} onChange={(e) => set({ status: e.target.value as TicketListFilters['status'] })}>
              {STATUS_VIEWS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label className={styles.control}>
            <span className={styles.controlLabel}>Assignee</span>
            <select className={styles.select} value={filters.assignee ?? ''} onChange={(e) => set({ assignee: e.target.value || undefined })}>
              <option value="">Anyone</option>
              <option value="me">Me</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </label>
          <label className={styles.control}>
            <span className={styles.controlLabel}>Priority</span>
            <select className={styles.select} value={filters.priority ?? ''} onChange={(e) => set({ priority: (e.target.value || undefined) as TicketPriority | undefined })}>
              <option value="">Any</option>
              {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <div className="relative min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder="#number, subject, customer…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8 h-9 text-xs"
            />
          </div>
          <Button onClick={() => setCreating(true)} className="gap-1.5 h-9">
            <Plus size={15} />
            <span>New ticket</span>
          </Button>
        </div>
      </header>

      {/* New Ticket Modal Dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create New Support Ticket</DialogTitle>
            <DialogDescription>
              Log an issue, feature inquiry, or escalation for your team.
            </DialogDescription>
          </DialogHeader>
          <NewTicketForm
            sites={sites}
            onCreated={(t) => {
              setCreating(false);
              void load();
              navigate(`/tickets/${t.id}`);
            }}
            onCancel={() => setCreating(false)}
          />
        </DialogContent>
      </Dialog>

      {error ? <p className={styles.error} role="alert">{error}</p> : null}

      <div className={`${own.layout} ${selectedId ? own.layoutWithDetail : ''}`}>
        <div className={styles.panel}>
          <div className="flex items-center justify-between pb-3 border-b border-border mb-3">
            <p className={styles.panelTitle}>{total} {total === 1 ? 'ticket' : 'tickets'}</p>
            <span className="text-xs text-muted-foreground">Showing up to {limit} per page</span>
          </div>

          {loading ? (
            <div className="space-y-3 py-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-xl my-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-3.5">
                <Inbox size={26} />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">No tickets match your filters</h3>
              <p className="text-xs text-muted-foreground max-w-xs mb-4">
                Try widening your search terms, changing priority, or clearing status restrictions.
              </p>
              <Button variant="outline" size="sm" onClick={() => { setQuery(''); setFilters({ status: 'all', page: 1 }); }}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className={styles.tableScroll}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[70px]">#</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assignee</TableHead>
                    <TableHead className="text-right">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow
                      key={t.id}
                      className={`cursor-pointer transition-colors ${t.id === selectedId ? 'bg-primary/10 font-medium' : 'hover:bg-muted/50'}`}
                      onClick={() => navigate(`/tickets/${t.id}`)}
                    >
                      <TableCell className="font-mono text-xs text-muted-foreground font-semibold">#{t.number}</TableCell>
                      <TableCell className="max-w-[320px]">
                        <div className="truncate font-medium text-foreground" title={t.subject}>
                          {t.subject}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">{t.siteName}</div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{t.visitorName || t.visitorEmail || '—'}</TableCell>
                      <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                      <TableCell><StatusBadge status={t.status} /></TableCell>
                      <TableCell className="text-xs">{t.assigneeName ?? <span className="text-muted-foreground italic">Unassigned</span>}</TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">{formatRelativeOrAbsolute(new Date(t.updatedAt))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border mt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: page - 1 }))}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => setFilters((f) => ({ ...f, page: page + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </div>

        {selectedId && (
          <TicketPanel key={selectedId} ticketId={selectedId} onChanged={() => void load()} onClose={() => navigate('/tickets')} />
        )}
      </div>
    </section>
  );
}

function NewTicketForm({
  sites,
  onCreated,
  onCancel,
}: {
  sites: SiteLite[];
  onCreated: (t: TicketRow) => void;
  onCancel: () => void;
}) {
  const [siteId, setSiteId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [assignee, setAssignee] = useState('auto');
  const [agents, setAgents] = useState<AgentLite[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId && sites[0]) setSiteId(sites[0].id);
  }, [sites, siteId]);

  useEffect(() => {
    if (!siteId) return;
    setAssignee('auto');
    listSiteAgents(siteId).then(setAgents).catch(() => setAgents([]));
  }, [siteId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { ticket } = await createTicket({
        siteId,
        subject,
        ...(description.trim() ? { description } : {}),
        priority,
        ...(assignee === 'auto' ? {} : { assigneeId: assignee || null }),
      });
      onCreated(ticket);
    } catch {
      setError('Could not create the ticket.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4 pt-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="ticket-site" className="text-xs">Site</Label>
          <select
            id="ticket-site"
            className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            required
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ticket-priority" className="text-xs">Priority</Label>
          <select
            id="ticket-priority"
            className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
          >
            {TICKET_PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ticket-assignee" className="text-xs">Assignee</Label>
          <select
            id="ticket-assignee"
            className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
          >
            <option value="auto">Assign automatically</option>
            <option value="">Unassigned</option>
            {agents.map((a) => (
              <option key={a.agentId} value={a.agentId}>{a.displayName}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ticket-subject" className="text-xs">Subject</Label>
        <Input
          id="ticket-subject"
          required
          maxLength={300}
          placeholder="Brief summary of the inquiry or issue..."
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="h-9 text-xs"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ticket-desc" className="text-xs">Description</Label>
        <Textarea
          id="ticket-desc"
          placeholder="Full details, steps to reproduce, or visitor context..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="min-h-[100px] text-xs resize-y"
        />
      </div>

      {error && <p className="text-xs text-destructive font-medium">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={busy || !siteId || !subject.trim()}>
          {busy ? 'Creating Ticket…' : 'Create Ticket'}
        </Button>
      </div>
    </form>
  );
}

function describeEvent(e: TicketEventRow, names: Record<string, string>): string {
  const who = e.actorName ?? 'System';
  const agent = (id: string | null) => (id ? names[id] ?? 'an agent' : 'nobody');
  switch (e.type) {
    case 'created': return `${who} created the ticket`;
    case 'status': return `${who} changed status ${e.fromValue} → ${e.toValue}`;
    case 'priority': return `${who} changed priority ${e.fromValue} → ${e.toValue}`;
    case 'subject': return `${who} renamed it to “${e.toValue}”`;
    case 'assignee':
      return e.actorId
        ? `${who} assigned it to ${agent(e.toValue)}`
        : `Auto-assigned to ${agent(e.toValue)}`;
    default: return who;
  }
}

function TicketPanel({ ticketId, onChanged, onClose }: { ticketId: string; onChanged: () => void; onClose: () => void }) {
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [agents, setAgents] = useState<AgentLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await getTicket(ticketId));
      setError(null);
    } catch (err) {
      setError(isApiError(err) && err.status === 404 ? 'Ticket not found.' : 'Could not load the ticket.');
    }
  }, [ticketId]);
  useEffect(() => { void load(); }, [load]);

  const siteId = detail?.ticket.siteId;
  useEffect(() => {
    if (!siteId) return;
    listSiteAgents(siteId).then(setAgents).catch(() => setAgents([]));
  }, [siteId]);

  async function patch(p: Parameters<typeof updateTicket>[1]) {
    setBusy(true);
    try {
      await updateTicket(ticketId, p);
      await load();
      onChanged();
    } catch {
      setError('Update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function submitComment(e: FormEvent) {
    e.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await addTicketComment(ticketId, comment);
      setComment('');
      await load();
      onChanged();
    } catch {
      setError('Comment failed.');
    } finally {
      setBusy(false);
    }
  }

  if (error && !detail) {
    return (
      <div className={styles.panel}>
        <p className={styles.error} role="alert">{error}</p>
        <button type="button" className={styles.viewBtn} onClick={onClose}>Close</button>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={styles.panel}>
        <p className={styles.empty}>Loading…</p>
      </div>
    );
  }

  const t = detail.ticket;
  return (
    <div className={styles.panel}>
      <div className={own.detail}>
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-semibold text-primary">#{t.number}</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{t.siteName}</span>
            </div>
            <h2 className="text-lg font-semibold text-foreground tracking-tight">{t.subject}</h2>
            <div className="text-xs text-muted-foreground mt-1">
              Opened {formatDateTime(new Date(t.createdAt))}
              {t.visitorName || t.visitorEmail ? ` · ${t.visitorName ?? ''} ${t.visitorEmail ? `<${t.visitorEmail}>` : ''}` : ''}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close ticket" className="h-8 w-8 text-muted-foreground hover:text-foreground">
            <X size={16} />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 my-3">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</Label>
            <select
              className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={t.status}
              disabled={busy}
              onChange={(e) => void patch({ status: e.target.value as TicketStatus })}
            >
              {TICKET_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Priority</Label>
            <select
              className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={t.priority}
              disabled={busy}
              onChange={(e) => void patch({ priority: e.target.value as TicketPriority })}
            >
              {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Assignee</Label>
            <select
              className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={t.assigneeId ?? ''}
              disabled={busy}
              onChange={(e) => void patch({ assigneeId: e.target.value || null })}
            >
              <option value="">Unassigned</option>
              {t.assigneeId && !agents.some((a) => a.agentId === t.assigneeId) && (
                <option value={t.assigneeId}>{t.assigneeName ?? 'Current assignee'}</option>
              )}
              {agents.map((a) => <option key={a.agentId} value={a.agentId}>{a.displayName}</option>)}
            </select>
          </div>
        </div>

        {t.sessionId && (
          <div className="mb-2">
            <Link className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-medium" to={`/conversation/${t.sessionId}`}>
              <MessageSquare size={13} />
              <span>Open live conversation transcript →</span>
            </Link>
          </div>
        )}

        {t.description && (
          <div className="p-3 rounded-lg bg-muted/40 border border-border text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed mb-2">
            {t.description}
          </div>
        )}

        {error && <p className="text-xs text-destructive font-medium">{error}</p>}

        <div className="pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Ticket History & Activity</p>
          <ul className={own.timeline}>
            {detail.events.map((e) =>
              e.type === 'comment' ? (
                <li key={e.id} className={own.comment}>
                  <span className={own.commentMeta}>{e.actorName ?? 'System'} · {formatDateTime(new Date(e.createdAt))}</span>
                  {e.body}
                </li>
              ) : (
                <li key={e.id} className={own.event}>
                  {describeEvent(e, detail.agentNames)} · {formatRelativeOrAbsolute(new Date(e.createdAt))}
                </li>
              ),
            )}
          </ul>
        </div>

        <form onSubmit={submitComment} className="space-y-2 pt-2 border-t border-border mt-2">
          <Textarea
            placeholder="Write internal note — visible only to agents..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="text-xs min-h-[64px]"
          />
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={busy || !comment.trim()}>
              {busy ? 'Saving…' : 'Add Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
